import type { ConversationType } from "owa-graph-schema";
import type ItemId from "owa-service/lib/contract/ItemId";
import type { TableView } from "owa-mail-list-store";
import { type TableQueryType } from "owa-mail-list-store";
import type TableViewConversationRelation from "owa-mail-list-store/lib/store/schema/TableViewConversationRelation";
import getShouldShowRSVPForConversationAndPrepareItem from "owa-listview-rsvp/lib/utils/getShouldShowRSVPForConversationAndPrepareItem";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import getConversationClientIdForSearchResult from "owa-mail-search/lib/utils/getConversationClientIdForSearchResult";
import isSearchResultMailboxCertain from "owa-mail-search/lib/utils/isSearchResultMailboxCertain";
import { isGlobalFeatureEnabled } from "owa-feature-flags";

function getEffectiveMentioned(conversationType: ConversationType): boolean {
    return conversationType.GlobalMentionedMe || conversationType.GlobalAtAllMention || false;
}

export default function mapConversationTypeToConversationRelation(conversationType: ConversationType, tableView: TableView): TableViewConversationRelation {
    // Resolve the mailbox the conversation's items actually live in. For search this can be
    // an aux-archive / cross-forest mailbox that differs from the table's mailbox, so it must
    // be used both for the relation and to target the RSVP prefetch GetItem.
    const isSearch = tableView?.tableQuery?.type === 1;
    const mailboxInfo = isSearch
        ? getConversationClientIdForSearchResult(conversationType, tableView).mailboxInfo
        : (conversationType.ConversationId.mailboxInfo ?? getMailboxInfo(tableView));
    // Non-search tables own their items, so the table mailbox is authoritative. A search result
    // is only certain when its mailbox was resolved authoritatively (archive fanout / explicit
    // mailboxInfo); otherwise it was defaulted to the table mailbox and must not be prefetched.
    const isMailboxCertain = !isGlobalFeatureEnabled("tri-rsvpPrefetch-mailboxRouting") ||
        (isSearch ? isSearchResultMailboxCertain(conversationType) : true);
    return {
        categories: conversationType.Categories ?? [],
        draftItemIds: conversationType.DraftItemIds
            ? conversationType.DraftItemIds.map(baseDraftItemId => (<ItemId>baseDraftItemId).Id)
            : [],
        effectiveMentioned: getEffectiveMentioned(conversationType),
        executeSearchSortOrder: conversationType.SortOrderSource ?? "DateTime",
        flagStatus: conversationType.FlagStatus ?? "NotFlagged",
        hasAttachments: !!conversationType.HasAttachments,
        isTaggedForBigScreen: !!conversationType.IsTaggedForBigScreen,
        hasIrm: !!conversationType.HasIrm,
        iconIndex: conversationType.IconIndex ?? "Default",
        id: conversationType.ConversationId.Id,
        importance: conversationType.Importance ?? "Normal",
        instanceKey: conversationType.InstanceKey ?? "",
        itemClasses: conversationType.ItemClasses ?? [],
        itemIds: conversationType.ItemIds?.map(baseItemId => (<ItemId>baseItemId).Id) ?? [],
        lastDeliveryOrRenewTimeStamp: conversationType.LastDeliveryOrRenewTime,
        lastDeliveryTimeStamp: conversationType.LastDeliveryTime,
        lastSentTimeStamp: conversationType.LastSentTime || conversationType.LastDeliveryTime, // LastSentTime is undefined for search result. Fallback to LastDeliveryTime
        lastModifiedTimeStamp: conversationType.LastModifiedTime ?? "",
        lastSender: conversationType.LastSender
            ? conversationType.LastSender
            : conversationType.From
                ? conversationType.From
                : undefined, // lastSender is null in search response, use From instead
        mailboxInfo,
        parentFolderId: conversationType.ParentFolderId ? conversationType.ParentFolderId.Id : "", // This value only exists in search items
        previewText: conversationType.Preview ?? "",
        returnTime: conversationType.ReturnTime ? conversationType.ReturnTime : "",
        uniqueRecipients: conversationType.UniqueRecipients ?? [],
        uniqueSenders: conversationType.UniqueSenders ?? [],
        unreadCount: conversationType.UnreadCount,
        shouldShowRSVP: getShouldShowRSVPForConversationAndPrepareItem(conversationType, tableView, mailboxInfo, isMailboxCertain),
        mailboxGuids: conversationType.MailboxGuids ?? [],
        instrumentationContext: null,
        conversationToMe: conversationType.ConversationToMe ?? null,
        conversationCcMe: conversationType.ConversationCcMe ?? null,
        copilotInboxScore: conversationType.ConversationCopilotInboxScore ?? -1,
        copilotNeedsReply: conversationType.ConversationCopilotNeedsReply ?? false,
        copilotInboxHeadline: undefined,
        copilotPriorityIsUserAdjusted: conversationType.ConversationCopilotPriorityIsUserAdjusted ?? false,
        flagDueDate: conversationType.ConversationFlagDueDate ?? null,
    };
}
